import React, { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Filter,
  Download,
  RefreshCw,
  Eye,
  Activity,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  HardDrive,
  Zap,
  Database,
  ShieldAlert,
  Trash2,
  UserCog,
  Pencil,
} from 'lucide-react'
import { api } from '@/lib/api'
import { auditApi } from '@/lib/audit-api'
import {
  AuditLogOptimized,
  AuditLogOptimizedFilters,
  AUDIT_OPERATIONS,
  AUDIT_TABLES,
  AUDIT_SOURCES,
  OPERATION_COLORS,
  SOURCE_COLORS,
  AuditOperation,
  AuditSource,
  AuditTable,
  ChangedField,
  AUDITED_FIELDS,
  CRITICAL_FIELDS,
} from '@/types/audit'
import { KPICard, type KPICardData } from '@/components/shared/KPICard'
import toast from 'react-hot-toast'

interface UserOption {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  role: string
}

// Quick-filter chips. Each writes a partial filter set on click; the page
// merges with the current filters. Designed around the questions an admin
// most often asks: "what manual edits happened today?", "who's been flipping
// sold_out?", etc.
const QUICK_FILTERS: {
  key: string
  label: string
  apply: (todayIso: string) => Partial<AuditLogOptimizedFilters>
}[] = [
  {
    key: 'manual-today',
    label: 'Manual edits today',
    apply: (today) => ({
      source: AuditSource.MANUAL,
      startDate: today,
      operation: undefined,
      table: undefined,
      changedField: undefined,
    }),
  },
  {
    key: 'sold-out-30d',
    label: 'Sold-out flips (30d)',
    apply: () => ({
      source: AuditSource.MANUAL,
      changedField: 'sold_out',
      table: 'project',
      operation: undefined,
      startDate: isoDaysAgo(30),
    }),
  },
  {
    key: 'role-changes',
    label: 'Role changes',
    apply: () => ({
      changedField: 'role',
      table: 'users',
      source: undefined,
      operation: undefined,
      startDate: isoDaysAgo(90),
    }),
  },
  {
    key: 'deletes',
    label: 'Deletes',
    apply: () => ({
      operation: 'DELETE',
      source: undefined,
      changedField: undefined,
      table: undefined,
      startDate: isoDaysAgo(30),
    }),
  },
]

function isoDaysAgo(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString().slice(0, 10)
}

function formatRelative(timestamp: string): string {
  const then = new Date(timestamp).getTime()
  const now = Date.now()
  const diffSec = Math.round((now - then) / 1000)
  if (diffSec < 60) return 'just now'
  const diffMin = Math.round(diffSec / 60)
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.round(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.round(diffHr / 24)
  if (diffDay < 7) return `${diffDay}d ago`
  return new Date(timestamp).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function isCriticalRow(log: AuditLogOptimized): boolean {
  if (log.operation === 'DELETE') return true
  if (!log.changed_fields) return false
  for (const k of Object.keys(log.changed_fields)) {
    if (CRITICAL_FIELDS.has(k)) return true
  }
  return false
}

function isDiff(v: unknown): v is ChangedField {
  return v !== null && typeof v === 'object' && 'old' in (v as object) && 'new' in (v as object)
}

function stringify(v: unknown): string {
  if (v === null || v === undefined) return 'null'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

function renderUserCell(log: AuditLogOptimized) {
  if (!log.user_id) {
    return <span className="text-gray-400 text-sm">—</span>
  }
  const fullName = [log.user_first_name, log.user_last_name].filter(Boolean).join(' ')
  const display = fullName || log.user_email || log.user_id.slice(0, 8)
  return (
    <div className="flex items-center space-x-2">
      <span className="text-sm text-gray-900" title={log.user_email || log.user_id}>
        {display}
      </span>
      {log.user_role && (
        <span
          className={`px-1.5 py-0.5 text-xs font-medium rounded ${
            log.user_role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
          }`}
        >
          {log.user_role}
        </span>
      )}
    </div>
  )
}

export function AuditOptimized() {
  // Default Source = manual. Scraper noise is ~99% of the table; admins want
  // human-attributable activity by default. They can clear the filter to see all.
  const [filters, setFilters] = useState<AuditLogOptimizedFilters>({
    page: 1,
    limit: 50,
    source: AuditSource.MANUAL,
  })

  const [showFilters, setShowFilters] = useState(true)
  const [selectedLog, setSelectedLog] = useState<AuditLogOptimized | null>(null)

  // Audit logs
  const { data: auditData, isLoading, refetch } = useQuery({
    queryKey: ['audit-logs-optimized', filters],
    queryFn: () => auditApi.getOptimizedAuditLogs(filters),
    keepPreviousData: true,
  })

  // Anomaly counts powering the KPI cards
  const { data: anomalies } = useQuery({
    queryKey: ['audit-anomalies'],
    queryFn: () => auditApi.getAnomalies(),
    staleTime: 60_000,
  })

  // User list for the User filter — small (handful of users), cached for 5min
  const { data: users } = useQuery<UserOption[]>({
    queryKey: ['users-list-for-audit'],
    queryFn: async () => {
      const r = await api.get('/auth/users')
      return r.data
    },
    staleTime: 5 * 60_000,
  })

  const handleFilterChange = (key: keyof AuditLogOptimizedFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      // Reset to page 1 when a filter changes, but NOT when paginating
      // (clicking Next/Prev) or changing page size.
      page: key === 'page' ? value : 1,
    }))
  }

  const applyQuickFilter = (key: string) => {
    const def = QUICK_FILTERS.find((q) => q.key === key)
    if (!def) return
    const today = new Date().toISOString().slice(0, 10)
    setFilters((prev) => ({
      ...prev,
      ...def.apply(today),
      page: 1,
    }))
  }

  const clearFilters = () => {
    setFilters({ page: 1, limit: 20 })
  }

  const handleExport = async () => {
    try {
      const blob = await auditApi.exportOptimizedAuditLogs(filters)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      toast.success('Audit logs exported')
    } catch {
      toast.error('Failed to export audit logs')
    }
  }

  const getOperationIcon = (operation: string) => {
    switch (operation) {
      case 'INSERT':
      case 'SCRAP_INSERT':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'UPDATE':
      case 'SCRAP_UPDATE':
        return <RefreshCw className="h-4 w-4 text-blue-500" />
      case 'DELETE':
        return <XCircle className="h-4 w-4 text-red-500" />
      case 'SCRAP_ERROR':
        return <AlertCircle className="h-4 w-4 text-red-500" />
      case 'SCRAP_WARNING':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />
      case 'SCRAP_SKIPPED':
        return <Clock className="h-4 w-4 text-gray-500" />
      default:
        return <Activity className="h-4 w-4 text-gray-500" />
    }
  }

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'scraper':
        return <Zap className="h-4 w-4 text-blue-500" />
      case 'manual':
        return <Database className="h-4 w-4 text-green-500" />
      case 'system':
        return <HardDrive className="h-4 w-4 text-gray-500" />
      default:
        return <Activity className="h-4 w-4 text-gray-500" />
    }
  }

  const renderChangedFields = (changedFields: Record<string, unknown> | null) => {
    if (!changedFields) return null
    const entries = Object.entries(changedFields)
    if (entries.length === 0) {
      return <span className="text-gray-400 text-sm italic">no field changes</span>
    }
    return (
      <div className="space-y-2">
        {entries.map(([field, change]) =>
          isDiff(change) ? (
            <div key={field} className="flex items-center space-x-2 text-sm">
              <span className="font-medium text-gray-700">{field}:</span>
              <span className="text-red-600 line-through">{stringify(change.old)}</span>
              <span className="text-gray-400">→</span>
              <span className="text-green-600 font-medium">{stringify(change.new)}</span>
            </div>
          ) : (
            <div key={field} className="flex items-center space-x-2 text-sm">
              <span className="font-medium text-gray-700">{field}:</span>
              <span className="text-gray-700">{stringify(change)}</span>
            </div>
          ),
        )}
      </div>
    )
  }

  const anomalyCards: KPICardData[] = useMemo(
    () => [
      {
        id: 'manual-24h',
        label: 'Manual edits',
        value: anomalies?.manualEditsLast24h ?? 0,
        detail: 'last 24h',
        icon: Pencil,
        color: 'blue',
        info: {
          definition: 'Audit rows where source = manual within the last 24 hours.',
          source: 'audit_logs_optimized (source = manual, timestamp >= now() - 24h)',
        },
      },
      {
        id: 'sold-out-7d',
        label: 'Sold-out flips',
        value: anomalies?.soldOutFlipsLast7d ?? 0,
        detail: 'last 7d',
        icon: ShieldAlert,
        color: 'amber',
        info: {
          definition:
            'Audit rows touching project.sold_out within the last 7 days (UPDATE or INSERT).',
          source: "audit_logs_optimized (table_name = 'project', changed_fields ? 'sold_out')",
        },
      },
      {
        id: 'role-changes-30d',
        label: 'Role changes',
        value: anomalies?.roleChangesLast30d ?? 0,
        detail: 'last 30d',
        icon: UserCog,
        color: 'purple',
        info: {
          definition: 'Audit rows touching users.role within the last 30 days.',
          source: "audit_logs_optimized (table_name = 'users', changed_fields ? 'role')",
        },
      },
      {
        id: 'deletes-7d',
        label: 'Deletes',
        value: anomalies?.recentDeletesLast7d ?? 0,
        detail: 'last 7d',
        icon: Trash2,
        color: 'red',
        info: {
          definition: 'Audit rows where operation = DELETE within the last 7 days.',
          source: "audit_logs_optimized (operation = 'DELETE', timestamp >= now() - 7d)",
        },
      },
    ],
    [anomalies],
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-sm text-gray-500 mt-1">
            Showing <span className="font-medium">{filters.source ?? 'all'}</span> activity
            {filters.userId && users && (
              <>
                {' by '}
                <span className="font-medium">
                  {users.find((u) => u.id === filters.userId)?.email ?? filters.userId}
                </span>
              </>
            )}
            {filters.changedField && (
              <>
                {' touching '}
                <code className="bg-gray-100 px-1 rounded">{filters.changedField}</code>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleExport}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
          <button
            onClick={() => refetch()}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Anomaly Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {anomalyCards.map((card) => (
          <KPICard key={card.id} card={card} />
        ))}
      </div>

      {/* Quick-filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider mr-1">
          Quick filters:
        </span>
        {QUICK_FILTERS.map((q) => (
          <button
            key={q.key}
            onClick={() => applyQuickFilter(q.key)}
            className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 hover:border-primary-400"
          >
            {q.label}
          </button>
        ))}
        <button
          onClick={clearFilters}
          className="px-3 py-1 text-xs font-medium text-gray-500 hover:text-gray-700"
        >
          Clear all
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <Filter className="h-4 w-4" />
            <span>{showFilters ? 'Hide' : 'Show'} Filters</span>
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Source</label>
              <select
                value={filters.source ?? ''}
                onChange={(e) => handleFilterChange('source', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Sources</option>
                {Object.entries(AUDIT_SOURCES).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">User</label>
              <select
                value={filters.userId ?? ''}
                onChange={(e) => handleFilterChange('userId', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Users</option>
                {users?.map((u) => (
                  <option key={u.id} value={u.id}>
                    {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Changed field</label>
              <select
                value={filters.changedField ?? ''}
                onChange={(e) => handleFilterChange('changedField', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Any field</option>
                {AUDITED_FIELDS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Table</label>
              <select
                value={filters.table || ''}
                onChange={(e) => handleFilterChange('table', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Tables</option>
                {Object.entries(AUDIT_TABLES).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Operation</label>
              <select
                value={filters.operation || ''}
                onChange={(e) => handleFilterChange('operation', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Operations</option>
                {Object.entries(AUDIT_OPERATIONS).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date range</label>
              <div className="flex space-x-2">
                <input
                  type="date"
                  value={filters.startDate || ''}
                  onChange={(e) => handleFilterChange('startDate', e.target.value || undefined)}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                />
                <input
                  type="date"
                  value={filters.endDate || ''}
                  onChange={(e) => handleFilterChange('endDate', e.target.value || undefined)}
                  className="flex-1 px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Audit Logs Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Audit Logs</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8" />
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Operation
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Table
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Record ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Changes
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                    Loading audit logs...
                  </td>
                </tr>
              ) : auditData?.data.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    No audit logs found
                  </td>
                </tr>
              ) : (
                auditData?.data.map((log) => {
                  const critical = isCriticalRow(log)
                  return (
                    <tr
                      key={log.id}
                      className={critical ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-50'}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        {critical && (
                          <span title="Critical field changed">
                            <ShieldAlert className="h-4 w-4 text-amber-600" />
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {getOperationIcon(log.operation)}
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              OPERATION_COLORS[log.operation as AuditOperation] ||
                              'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {AUDIT_OPERATIONS[log.operation as AuditOperation] || log.operation}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {AUDIT_TABLES[log.table_name as AuditTable] || log.table_name}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                        {log.record_id}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {getSourceIcon(log.source || '')}
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              SOURCE_COLORS[log.source as AuditSource] ||
                              'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {AUDIT_SOURCES[log.source as AuditSource] || log.source || 'Unknown'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">{renderUserCell(log)}</td>
                      <td className="px-4 py-4 text-sm text-gray-700 max-w-md truncate">
                        {log.change_summary || (
                          <span className="text-gray-400 italic">no summary</span>
                        )}
                      </td>
                      <td
                        className="px-4 py-4 whitespace-nowrap text-sm text-gray-500"
                        title={new Date(log.timestamp).toLocaleString()}
                      >
                        {formatRelative(log.timestamp)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          onClick={() => setSelectedLog(log)}
                          className="text-primary-600 hover:text-primary-700 flex items-center space-x-1"
                        >
                          <Eye className="h-4 w-4" />
                          <span>View</span>
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {auditData?.meta && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-gray-700">
                Showing {((filters.page || 1) - 1) * (filters.limit || 50) + 1} to{' '}
                {Math.min((filters.page || 1) * (filters.limit || 50), auditData.meta.total)} of{' '}
                {auditData.meta.total.toLocaleString()} results
              </div>
              <div className="flex items-center space-x-3">
                <label className="text-sm text-gray-600 flex items-center space-x-2">
                  <span>Per page:</span>
                  <select
                    value={filters.limit ?? 50}
                    onChange={(e) => handleFilterChange('limit', Number(e.target.value))}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {[20, 50, 100, 200].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleFilterChange('page', Math.max(1, (filters.page || 1) - 1))}
                    disabled={(filters.page || 1) <= 1}
                    className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="px-2 py-1 text-sm text-gray-600">
                    Page {filters.page || 1} of {auditData.meta.totalPages}
                  </span>
                  <button
                    onClick={() => handleFilterChange('page', (filters.page || 1) + 1)}
                    disabled={(filters.page || 1) >= auditData.meta.totalPages}
                    className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Audit Log Details</h3>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Basic Information</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">ID:</span>
                      <span className="text-sm font-mono text-gray-900">{selectedLog.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Table:</span>
                      <span className="text-sm text-gray-900">
                        {AUDIT_TABLES[selectedLog.table_name as AuditTable] ||
                          selectedLog.table_name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Record ID:</span>
                      <span className="text-sm font-mono text-gray-900">
                        {selectedLog.record_id}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Operation:</span>
                      <span className="text-sm text-gray-900">
                        {AUDIT_OPERATIONS[selectedLog.operation as AuditOperation] ||
                          selectedLog.operation}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Source:</span>
                      <span className="text-sm text-gray-900">
                        {AUDIT_SOURCES[selectedLog.source as AuditSource] ||
                          selectedLog.source ||
                          'Unknown'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Timestamp:</span>
                      <span className="text-sm text-gray-900">
                        {new Date(selectedLog.timestamp).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Change Summary</h4>
                  <div className="bg-gray-50 rounded-md p-3">
                    <p className="text-sm text-gray-900">
                      {selectedLog.change_summary || 'No summary available'}
                    </p>
                  </div>
                </div>
              </div>

              {selectedLog.changed_fields && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Changed Fields</h4>
                  <div className="bg-gray-50 rounded-md p-4">
                    {renderChangedFields(selectedLog.changed_fields)}
                  </div>
                </div>
              )}

              {(selectedLog.user_id || selectedLog.ip_address || selectedLog.user_agent) && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Additional Information</h4>
                  <div className="space-y-2">
                    {selectedLog.user_id && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">User:</span>
                        <span className="text-sm text-gray-900">
                          {[selectedLog.user_first_name, selectedLog.user_last_name]
                            .filter(Boolean)
                            .join(' ') ||
                            selectedLog.user_email ||
                            selectedLog.user_id}
                          {selectedLog.user_role && (
                            <span className="ml-2 px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-700">
                              {selectedLog.user_role}
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                    {selectedLog.user_id && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">User ID:</span>
                        <span className="text-sm font-mono text-gray-900">
                          {selectedLog.user_id}
                        </span>
                      </div>
                    )}
                    {selectedLog.ip_address && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">IP Address:</span>
                        <span className="text-sm font-mono text-gray-900">
                          {selectedLog.ip_address}
                        </span>
                      </div>
                    )}
                    {selectedLog.user_agent && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">User Agent:</span>
                        <span className="text-sm text-gray-900">{selectedLog.user_agent}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
